import { unreachableCase } from 'frontend/types';
import { ElmCompiledAsset, LessonAssetTypeName } from 'frontend/types/lessons/editor';

/* In sync with  elmConfigPrelude inside of CodeGen.elm
 *
 */
const ELM_CONFIG_IMPORTS = `
import Interactive exposing (ElmConfigInitializer(..))
import Vector exposing (Vector, v2)
`;

const ELM_CONFIG_PREVIEW_HEADER = `
module Main exposing (..)

import EditorPreview.ElmConfig
${ELM_CONFIG_IMPORTS}
`;

// config here comes from the expected API within the elmconfig asset creator
const ELM_CONFIG_PREVIEW_FOOTER = `
main =
  EditorPreview.ElmConfig.interactivePreview config
`;

// TODO: Make this the single canonical location of diagrammar imports etc.
// Generate or pass these values into the following files to remove duplication:
// frontend/elm/lessons/transpiler/BrilliantAST/CodeGen.elm
//    pass DIAGRAMMAR_IMPORTS into transpiler as a flag at initialization time
//    (Elm.BrilliantAST.Compiler.init)
// interactives/packages/diagrammar/templates/Header.elm.template
// interactives/packages/diagrammar/templates/Footer.elm.template
// interactives/packages/diagrammar/templates/SolvableFooter.elm.template
//    Generate template files for use by the V2 diagrammar compiler
//    (brilliant.visualizations.views.DiagrammarCompilationView)

const DIAGRAMMAR_IMPORTS = `
import Bits.Colors
import Diagrammar exposing (..)
import Diagrammar.Animation as Animation
import Diagrammar.Annotations as Annotations
import Diagrammar.Annotations.Angle as Angle exposing (Angle)
import Diagrammar.Annotations.Arrow as Arrow exposing (Arrow)
import Diagrammar.Annotations.Marker as Marker
import Diagrammar.App exposing (InputValues)
import Diagrammar.Color as Color exposing (Color, ColorFamily)
import Diagrammar.Csv as Csv
import Diagrammar.DropShadow as DropShadow exposing (DropShadow)
import Diagrammar.Input as Input exposing (Input)
import Diagrammar.Input.External as External
import Diagrammar.Input.Locator as Locator exposing (Locator)
import Diagrammar.Input.ModeSwitcher as ModeSwitcher exposing (ModeSwitcher)
import Diagrammar.Input.Solvable as Solvable
import Diagrammar.Input.Slider as Slider exposing (Slider)
import Diagrammar.Input.SteppedSlider as SteppedSlider exposing (SteppedSlider)
import Diagrammar.Input.Stepper as Stepper exposing (Stepper)
import Diagrammar.Input.Time as Time exposing (Time)
import Diagrammar.Input.Toggle as Toggle exposing (Toggle)
import Diagrammar.Label as Label exposing (Label)
import Diagrammar.Output as Output exposing (OutputDiagram)
import Diagrammar.Plot2 as Plot2 exposing (Plot2)
import Diagrammar.Plot2.Axis as Axis exposing (Axis)
import Diagrammar.Plot2.Grid as Grid exposing (Grid)
import Diagrammar.Plot2.GridD as GridD
import Diagrammar.Plot2.Legend as Legend exposing (Legend)
import Diagrammar.Plot2.Ticks as Ticks
import Diagrammar.Private.Render
import Diagrammar.Random as Random
import Diagrammar.Sizing as Sizing
import Diagrammar.TextBox as TextBox exposing (TextBox)
import Diagrammar.TextLabel as TextLabel exposing (TextLabel)
import Math exposing (..)
import Misc
import Random
import Range exposing (Range, range)
import String.FloatString as FloatString
import String.IntString as IntString
import Vector exposing (Vector, v2, v3)
`;

export const DIAGRAMMAR_HEADER = `
module Main exposing (..)

import Diagrammar.Embed
${DIAGRAMMAR_IMPORTS}
`;

function diagrammarFooter(isSolvable: boolean): string {
  const mainReturnType = isSolvable ? '(Just onCheck)' : 'Nothing';

  return `
main =
  Diagrammar.Embed.interactiveDiagrammar diagramInputs diagram ${mainReturnType}
`;
}

function simsonHeaderWithModuleName(moduleName: string, userLibs: string[]) {
  const packageNames = userLibs.map(lib => lib.split('.')[0]);
  const simsonImports = packageNames.includes('Simson')
    ? ''
    : `
import Simson.Embed
import Simson.Pills exposing (..)
import Simson.Toolbox as Toolbox exposing (Toolbox)
import Simson.Simson as Simson exposing (Simson, Program)
import Simson.Visualization as Visualization exposing (Visualization, VisualizationInputs)
import Simson.ExecutionSpeed as ExecutionSpeed
`;

  return `
module ${moduleName} exposing (..)

${simsonImports}

-- We import diagrammar things for writing diagramms in the visualization
${DIAGRAMMAR_IMPORTS}
`;
}

const SIMSON_FOOTER = `
main =
    Simson.Embed.interactiveProgram config
`;

export function userLibImports(userLibs: string[]): string {
  return userLibs
    .map((l: string) => `import BrilliantUniverse.${l}.${l.split('.')[0]} as ${l.split('.')[0]}`)
    .join('\n');
}

function diagrammarHeader(userLibs: string[] = []): string {
  return `
${DIAGRAMMAR_HEADER}
${userLibImports(userLibs)}
`.trim();
}

function elmconfigHeader(userLibs: string[] = []): string {
  return `
${ELM_CONFIG_PREVIEW_HEADER}
${userLibImports(userLibs)}
`.trim();
}

function simsonHeader(moduleName: string, userLibs: string[] = []): string {
  return `
${simsonHeaderWithModuleName(moduleName, userLibs)}
${userLibImports(userLibs)}
`.trim();
}

function simsonFooter(userLibs: string[]): string {
  const packageNames = userLibs.map(lib => lib.split('.')[0]);
  return packageNames.includes('Simson') ? '' : SIMSON_FOOTER;
}

interface CompilableElmArgs {
  // Optional while this is used by legacy code.
  moduleName?: string;
  elm: string;
  userLibs: string[];
  isSolvable: boolean;
  assetType: LessonAssetTypeName;
  alias: string;
}

export function compilableElm(args: CompilableElmArgs): string {
  const { moduleName, elm, userLibs, isSolvable, assetType, alias } = args;

  switch (assetType) {
    case 'elmconfig':
      return `
${elmconfigHeader(userLibs)}
${elm}
${ELM_CONFIG_PREVIEW_FOOTER}
`.trim();
    case 'diagrammar':
      return `
${diagrammarHeader(userLibs)}
${elm}
${diagrammarFooter(isSolvable)}
`.trim();
    case 'simson':
      return `
${simsonHeader(moduleName || 'Main', userLibs)}
${elm}
${simsonFooter(userLibs)}
`.trim();
    default:
      throw new Error(`Can't compile asset of type: '${assetType}'`);
  }
}

export function headerLineCount(asset: ElmCompiledAsset): number {
  const { userLibs, type } = asset;
  switch (type) {
    case 'elmconfig':
      return elmconfigHeader(userLibs).split('\n').length;
    case 'diagrammar':
      return diagrammarHeader(userLibs).split('\n').length;
    case 'simson':
      return simsonHeader('SomeModuleNameJustToCountLines', userLibs).split('\n').length;
    default:
      unreachableCase(type);
  }
}

export function computeModuleName(authoredName: string, assetIndex: number) {
  // e.g. "Interactive_12_authoredXname"
  return `Interactive_${assetIndex}_${safeElmId(authoredName)}`;
}

const ALPHA_NUMERIC = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

function safeElmId(authoredName: string) {
  const authNameArray = [...authoredName];
  const safeArray = authNameArray.map(char => {
    if (ALPHA_NUMERIC.includes(char)) {
      return char.toLowerCase();
    }

    if (char === ' ') {
      return '_';
    }

    return 'X';
  });

  return safeArray.join('');
}
